import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { useTheme } from '../../lib/theme';

export interface DonutDatum {
  name: string;
  value: number;
  status: 'open' | 'accepted' | 'completed' | 'cancelled' | 'expired';
}

interface Props {
  data: DonutDatum[];
}

const COLORS_LIGHT: Record<DonutDatum['status'], string> = {
  open: '#F59E0B',
  accepted: '#3B82F6',
  completed: '#16A34A',
  cancelled: '#A1A1AA',
  expired: '#71717A',
};
const COLORS_DARK: Record<DonutDatum['status'], string> = {
  open: '#FBBF24',
  accepted: '#60A5FA',
  completed: '#22C55E',
  cancelled: '#A1A1AA',
  expired: '#909097',
};

export function StatusDonut({ data }: Props) {
  const { resolved } = useTheme();
  const dark = resolved === 'dark';
  const palette = dark ? COLORS_DARK : COLORS_LIGHT;
  const total = data.reduce((s, d) => s + d.value, 0);
  const tooltipBg = dark ? '#171717' : '#FFFFFF';
  const tooltipBorder = dark ? '#262626' : '#E4E4E7';
  const text = dark ? '#FAFAFA' : '#0A0A0A';

  return (
    <div className="flex items-center gap-4">
      <div className="relative h-44 w-44 shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              innerRadius={50}
              outerRadius={75}
              paddingAngle={2}
              strokeWidth={0}
            >
              {data.map((d) => (
                <Cell key={d.status} fill={palette[d.status]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                background: tooltipBg,
                border: `1px solid ${tooltipBorder}`,
                borderRadius: 10,
                color: text,
                fontSize: 12,
                padding: '6px 10px',
                boxShadow: '0 10px 24px rgba(0,0,0,0.10)',
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <p className="tnum text-2xl font-bold tracking-tight text-text">{total.toLocaleString()}</p>
          <p className="text-[10px] font-medium uppercase tracking-wider text-text-muted">Total</p>
        </div>
      </div>
      <ul className="flex-1 space-y-2 text-sm">
        {data.map((d) => {
          const pct = total > 0 ? Math.round((d.value / total) * 100) : 0;
          return (
            <li key={d.status} className="flex items-center gap-2">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ background: palette[d.status] }}
              />
              <span className="capitalize text-text-subtle">{d.name}</span>
              <span className="ml-auto tnum font-medium text-text">{d.value}</span>
              <span className="tnum w-10 text-right text-xs text-text-muted">{pct}%</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
